import { useEffect, useState } from "react";
import GradientText from "../components/GradientText";
import Seo from "../components/Seo";

const neonColors = ["#fc03f4", "#03f0fc", "#fc03f4", "#03f0fc", "#fc03f4"];

type EventType = "food_truck_night" | "festival" | "private_event";

interface Event {
  id: string;
  name?: string;
  type: EventType;
  dates?: string[];
  location?: string;
  description?: string;
  image?: string;
  eventLink?: string;
}

interface EventsData {
  events: Event[];
}

const eventTypeConfig: Record<
  EventType,
  { label: string; emoji: string; gradient: string; borderColor: string }
> = {
  food_truck_night: {
    label: "Food Truck Night",
    emoji: "🚚",
    gradient: "from-orange-500 to-amber-600",
    borderColor: "border-orange-500",
  },
  festival: {
    label: "Festival",
    emoji: "🎪",
    gradient: "from-purple-500 to-fuchsia-600",
    borderColor: "border-purple-500",
  },
  private_event: {
    label: "Private Event",
    emoji: "🎉",
    gradient: "from-sky-500 to-cyan-600",
    borderColor: "border-sky-500",
  },
};

function getNextDate(dates: string[]): Date | null {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const futureDates = dates
    .map((d) => new Date(d + "T00:00:00"))
    .filter((d) => d >= now)
    .sort((a, b) => a.getTime() - b.getTime());

  return futureDates.length > 0 ? futureDates[0] : null;
}

function getUpcomingDates(dates: string[]): Date[] {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  return dates
    .map((d) => new Date(d + "T00:00:00"))
    .filter((d) => d >= now)
    .sort((a, b) => a.getTime() - b.getTime());
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getGoogleMapsUrl(location: string): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    location,
  )}`;
}

export default function EventsPage() {
  const [eventsData, setEventsData] = useState<EventsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);

  useEffect(() => {
    async function loadEvents() {
      try {
        const response = await fetch("/events.json");
        if (!response.ok) throw new Error("Failed to load events");
        const data = await response.json();
        setEventsData(data);
        setLoading(false);
      } catch (err) {
        console.error("Error loading events:", err);
        setError(true);
        setLoading(false);
      }
    }
    loadEvents();
  }, []);

  // Sort events by their next upcoming date
  const sortedEvents =
    eventsData?.events
      .map((event) => ({
        ...event,
        nextDate: event.dates ? getNextDate(event.dates) : null,
      }))
      .filter((event) => event.nextDate !== null) // Only show events with future dates
      .sort((a, b) => {
        if (!a.nextDate || !b.nextDate) return 0;
        return a.nextDate.getTime() - b.nextDate.getTime();
      }) || [];

  const handleCardClick = (eventId: string) => {
    setExpandedEvent(expandedEvent === eventId ? null : eventId);
  };

  return (
    <>
      <Seo
        title="Events"
        description="See upcoming Bull on the Loose food truck events and locations. Find where we're serving next!"
        path="/events"
        image="/images/TruckImage.webp"
      />
      {/* Page Header */}
      <section className="relative pt-[calc(80px+60px)] pb-[60px] px-6 text-center overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 page-header-gradient-bg"></div>
          <div className="absolute inset-0 hero-grid-bg"></div>
        </div>
        <div className="container">
          <GradientText
            colors={neonColors}
            animationSpeed={6}
            className="text-[clamp(2.5rem,8vw,4.5rem)] mb-4 font-display font-semibold"
          >
            Upcoming Events
          </GradientText>
          <p className="text-xl text-text-secondary font-light">
            Find us around town or book us for your next event
          </p>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-[60px] pb-[100px] bg-bg-secondary min-h-[60vh]">
        <div className="container">
          {/* Disclaimer */}
          <div className="bg-bg-card border border-border-default rounded-xl p-4 mb-10 max-w-[800px] mx-auto">
            <p className="text-text-secondary text-sm text-center flex items-center justify-center gap-2">
              <span className="text-lg">📅</span>
              <span>
                <strong className="text-text-primary">Note:</strong> Dates and
                locations are subject to change. Follow us on{" "}
                <a
                  href="https://www.facebook.com/BullOnTheLoose"
                  target="_blank"
                  rel="noopener"
                  className="text-accent-pink hover:text-accent-pink-light transition-colors"
                >
                  Facebook
                </a>{" "}
                for the most up-to-date information!
              </span>
            </p>
          </div>

          {/* Events Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1100px] mx-auto">
            {loading && (
              <div className="col-span-full text-center py-20 text-text-secondary">
                <div className="loading-spinner"></div>
                <p>Loading events...</p>
              </div>
            )}

            {error && (
              <div className="col-span-full text-center py-20">
                <span className="text-[4rem] block mb-5">😅</span>
                <h3 className="font-body font-semibold text-text-primary mb-2">
                  Oops! Couldn't load events
                </h3>
                <p className="text-text-secondary">
                  Please try refreshing the page
                </p>
              </div>
            )}

            {!loading && !error && sortedEvents.length === 0 && (
              <div className="col-span-full text-center py-20">
                <span className="text-[4rem] block mb-5">📆</span>
                <h3 className="font-body font-semibold text-text-primary mb-2">
                  No upcoming events
                </h3>
                <p className="text-text-secondary">
                  Check back soon for new events!
                </p>
              </div>
            )}

            {!loading &&
              !error &&
              sortedEvents.map((event, index) => (
                <EventCard
                  key={event.id}
                  event={event}
                  index={index}
                  isExpanded={expandedEvent === event.id}
                  onClick={() => handleCardClick(event.id)}
                />
              ))}
          </div>
        </div>
      </section>
    </>
  );
}

interface EventCardProps {
  event: Event & { nextDate: Date | null };
  index: number;
  isExpanded: boolean;
  onClick: () => void;
}

function EventCard({ event, index, isExpanded, onClick }: EventCardProps) {
  const [visible, setVisible] = useState(false);
  const [showAllDates, setShowAllDates] = useState(false);
  const config = eventTypeConfig[event.type];
  const upcomingDates = event.dates ? getUpcomingDates(event.dates) : [];
  const hasImage = event.image && event.image.trim() !== "";
  const isPrivate = event.type === "private_event";

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), index * 80 + 50);
    return () => clearTimeout(timer);
  }, [index]);

  // Reset showAllDates when card is collapsed
  useEffect(() => {
    if (!isExpanded) {
      setShowAllDates(false);
    }
  }, [isExpanded]);

  return (
    <div
      className={`menu-item-animate bg-bg-card border border-border-default rounded-xl overflow-hidden transition-all duration-300 cursor-pointer hover:border-accent-purple hover:shadow-md-custom hover:-translate-y-1 ${
        visible ? "visible" : ""
      } ${isExpanded ? "ring-2 ring-accent-pink" : ""}`}
      onClick={onClick}
    >
      {/* Image */}
      {hasImage && (
        <div className="w-full h-[160px] overflow-hidden relative bg-bg-elevated">
          <img
            src={event.image}
            alt={event.name || "Event"}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-400 hover:scale-105"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.parentElement!.innerHTML =
                '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-bg-elevated to-bg-tertiary"><span class="text-[2.5rem] opacity-50">🍖</span></div>';
            }}
          />
          {/* Event Type Badge */}
          <div
            className={`absolute top-3 left-3 px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${config.gradient} shadow-md`}
          >
            {config.emoji} {config.label}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-5">
        {/* Badge if no image */}
        {!hasImage && (
          <div
            className={`inline-flex px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${config.gradient} shadow-md mb-3`}
          >
            {config.emoji} {config.label}
          </div>
        )}

        {/* Event Name */}
        {event.name && (
          <h3 className="font-display font-semibold text-xl text-text-primary mb-3">
            {event.name}
          </h3>
        )}

        {/* Dates */}
        {upcomingDates.length > 0 && (
          <div className="flex flex-col gap-1 mb-3">
            <div className="flex items-center gap-2 text-accent-blue">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span className="text-sm font-medium">
                {upcomingDates.length === 1
                  ? formatDate(upcomingDates[0])
                  : `${upcomingDates.length} upcoming dates`}
              </span>
            </div>
            {upcomingDates.length > 1 && !isExpanded && (
              <p className="text-xs text-text-muted ml-6">
                Next: {formatDate(upcomingDates[0])}
              </p>
            )}
          </div>
        )}

        {/* Location (preview) */}
        {event.location && !isPrivate && (
          <a
            href={getGoogleMapsUrl(event.location)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-start gap-2 text-text-secondary hover:text-accent-blue transition-colors group"
          >
            <svg
              className="w-4 h-4 mt-0.5 flex-shrink-0 group-hover:text-accent-blue"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className="text-sm line-clamp-1 group-hover:underline">
              {event.location}
            </span>
          </a>
        )}

        {/* Click hint */}
        {!isExpanded && (
          <p className="text-xs text-text-muted mt-3 flex items-center gap-1">
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
              />
            </svg>
            Click for details
          </p>
        )}

        {/* Expanded Content */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-border-default animate-fade-in">
            {/* All dates for multi-date events */}
            {upcomingDates.length > 1 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-text-primary mb-2">
                  All Upcoming Dates:
                </p>
                <div className="flex flex-wrap gap-2">
                  {(showAllDates
                    ? upcomingDates
                    : upcomingDates.slice(0, 5)
                  ).map((date, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-bg-elevated rounded text-xs text-text-secondary"
                    >
                      {formatDate(date)}
                    </span>
                  ))}
                  {upcomingDates.length > 5 && !showAllDates && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAllDates(true);
                      }}
                      className="px-2 py-1 bg-bg-elevated hover:bg-border-default rounded text-xs text-accent-blue hover:text-accent-blue-light transition-colors cursor-pointer"
                    >
                      +{upcomingDates.length - 5} more
                    </button>
                  )}
                  {showAllDates && upcomingDates.length > 5 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAllDates(false);
                      }}
                      className="px-2 py-1 bg-bg-elevated hover:bg-border-default rounded text-xs text-accent-pink hover:text-accent-pink-light transition-colors cursor-pointer"
                    >
                      Show less
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            {event.description && (
              <p className="text-sm text-text-secondary leading-relaxed mb-3">
                {event.description}
              </p>
            )}

            {/* Event link button */}
            {event.eventLink && (
              <a
                href={event.eventLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-accent-pink to-accent-purple text-white text-sm font-medium rounded-full hover:shadow-md transition-all hover:-translate-y-0.5"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                View Event
              </a>
            )}

            {/* Contact for private events */}
            {isPrivate && !event.eventLink && (
              <a
                href="/contact"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-accent-pink to-accent-purple text-white text-sm font-medium rounded-full hover:shadow-md transition-all hover:-translate-y-0.5"
              >
                <span>📞</span>
                Book Your Event
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
